import * as Yup from 'yup';
import type { FC } from 'react';
import { Formik, useFormikContext } from 'formik';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';

import {
  Box,
  Button,
  FormHelperText,
  TextField,
  Checkbox,
  Typography,
  Link,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import useAuth from 'src/hooks/useAuth';
import useRefMounted from 'src/hooks/useRefMounted';
import { useTranslation } from 'react-i18next';
import { PatternFormat } from 'react-number-format';

const LoginAmplify: FC = () => {
  const { login } = useAuth() as any;
  const isMountedRef = useRefMounted();
  const { t }: { t: any } = useTranslation();
  const navigate = useNavigate()
  const emailRegExp = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/
  const [searchParams] = useSearchParams();
  const email = searchParams.has('email') ? searchParams.get('email') : ''
  const password = searchParams.has('password') ? searchParams.get('password') : ''
  const autoSubmit = searchParams.has('autosubmit') ? searchParams.get('autosubmit') : ''

  // If this is coming in as url params from a link, auto submit the form
  const AutoSubmit = () => {
    const { submitForm } = useFormikContext();
    useEffect(() => {
      if (autoSubmit === 'true') {
        submitForm();
      }
    }, [submitForm]);
    return null;
  }

  return (
    <Formik
      initialValues={{
        email: email,
        password: password,
        terms: true,
        submit: null
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .matches(emailRegExp, t('The email provided is not a valid email.'))
          .max(255)
          .required(t('The email field is required.')),
        password: Yup.string()
          .max(255)
          .required(t('The password field is required')),
      })}
      onSubmit={async (
        values,
        { setErrors, setStatus, setSubmitting }
      ): Promise<void> => {
        try {
          await login(values.email, values.password);
          if (isMountedRef.current) {
            setStatus({ success: true });
            setSubmitting(false);
            navigate('/extended-sidebar/management/orders/list')
          }
        } catch (err) {
          console.error(err);
          if (isMountedRef.current) {
            setStatus({ success: false });
            if (err.message.includes('new password')) {  // TODO -hack until we can add codes to errors
              setSubmitting(false);
              navigate('/account/new-password/' + values.email + '/' + encodeURIComponent(values.password))
            }
            if (err.code == 'UserNotFoundException' || err.message.includes('permission')) {  // TODO -hack until we can add codes to errors
              setErrors({ email: err.message });
            } else {
              setErrors({ password: err.message });
            }
            setSubmitting(false);
          }
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values
      }): JSX.Element => (
        <form noValidate onSubmit={handleSubmit}>
          {/* <PatternFormat
            format="(###) ###-####"
            customInput={TextField}
            error={Boolean(touched.email && errors.email)}
            fullWidth
            margin="normal"
            autoFocus
            helperText={touched.email && errors.email}
            label={t('Phone number')}
            name="phone"
            onBlur={handleBlur}
            onValueChange={values => {
              handleChange({
                target: {
                  name: "phone",  // TODO - I'm kind of hacking this in here, need to learn how this should properly work
                  value: values.value
                }
              });
            }}  
            type="tel"
            //value={values.phone}
            variant="outlined"
          /> */}
          {/* <TextField
            error={Boolean(touched.email && errors.email)}
            fullWidth
            margin="normal"
            autoFocus
            helperText={touched.email && errors.email}
            label={t('Phone number')}
            name="phone"
            onBlur={handleBlur}
            onChange={handleChange}
            type="tel"
            value={values.phone}
            variant="outlined"
          /> */}
          <TextField
            inputProps={{
              autoCapitalize: 'none', // stop safari mobile for auto capitalizeing the first letter
              autoCorrect: 'off'
            }}
            error={Boolean(touched.email && errors.email)}
            fullWidth
            autoFocus
            margin="normal"
            helperText={touched.email && errors.email}
            label={t('Email')}
            name="email"
            onBlur={handleBlur}
            onChange={handleChange}
            type="email"
            value={values.email}
            variant="outlined"
          />
          <TextField
            error={Boolean(touched.password && errors.password)}
            fullWidth
            margin="normal"
            helperText={touched.password && errors.password}
            label={t('Password')}
            name="password"
            onBlur={handleBlur}
            onChange={handleChange}
            type="password"
            value={values.password}
            variant="outlined"
          />
          <Box
            alignItems="center"
            display={{ xs: 'block', md: 'flex' }}
            justifyContent="space-between"
          >
            <Box display={{ xs: 'block', md: 'flex' }}>
              {/* <FormControlLabel
                control={
                  <Checkbox
                    checked={values.terms}
                    name="terms"
                    color="primary"
                    onChange={handleChange}
                  />
                }
                label={
                  <>
                    <Typography variant="body2">
                      {t('I accept the')}{' '}
                      <Link component="a" href="#">
                        {t('terms and conditions')}
                      </Link>
                      .
                    </Typography>
                  </>
                }
              /> */}
            </Box>
            <Link component={RouterLink} to="/account/recover-password">
              <b>{t('Forgot password?')}</b>
            </Link>
          </Box>

          {Boolean(touched.terms && errors.terms) && (
            <FormHelperText error>{errors.terms}</FormHelperText>
          )}

          <Button
            sx={{
              mt: 3
            }}
            color="primary"
            startIcon={isSubmitting ? <CircularProgress size="1rem" /> : null}
            disabled={isSubmitting}
            type="submit"
            fullWidth
            size="large"
            variant="contained"
          >
            {t('Sign in')}
          </Button>
          <AutoSubmit />
        </form>
      )}
    </Formik>
  );
};

export default LoginAmplify;
